import {
  Colors,
  type ColumnData,
  ImageGallery,
  type ImageGalleryImage,
} from '@gripp/shared-ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { StyleSheet, View } from 'react-native';

const PANEL_SOURCE =
  'gripp-workspace/apps/gripp-web/src/issues/components/useRoutineImagePanel.tsx';

type RoutineImageSelection = {
  images: ImageGalleryImage[];
  initialIndex: number;
  /** Distinguishes one open from the next; see the gallery's `key` below. */
  openId: number;
};

/**
 * Side panel for the images on a submitted routine answer, so a reviewer reads
 * the rest of the submission alongside the photo.
 *
 * Panel state rather than a route: the viewer has no identity worth addressing,
 * and every caller already owns its own column stack. The column is `null` while
 * nothing is open, so a caller can drop it straight into its slot math.
 *
 * `resetKey` is the id of whatever the panel hangs off (the activity being
 * reviewed). Callers keep this hook mounted across a change of that id, so
 * without the reset the panel would keep showing the previous submission's photo
 * beside the new one.
 */
export const useRoutineImagePanel = (resetKey?: string) => {
  const [selection, setSelection] = useState<RoutineImageSelection | null>(
    null
  );
  const [activeIndex, setActiveIndex] = useState(0);
  const openCountRef = useRef(0);

  const onViewImages = useCallback(
    (images: ImageGalleryImage[], initialIndex: number) => {
      openCountRef.current += 1;
      setSelection({ images, initialIndex, openId: openCountRef.current });
      setActiveIndex(initialIndex);
    },
    []
  );

  const closeImagePanel = useCallback(() => setSelection(null), []);

  useEffect(() => {
    setSelection(null);
  }, [resetKey]);

  const imageColumn = useMemo<ColumnData | null>(() => {
    if (!selection) return null;

    return {
      title: 'routines.admin.image',
      rawTitle: selection.images[activeIndex]?.name,
      showBackButton: true,
      onBackButtonPress: closeImagePanel,
      disableScroll: true,
      mainComponent: (
        <View style={styles.container} testID="routine-image-panel">
          <ImageGallery
            // ImageGallery seeds its own index from `initialIndex` on mount only,
            // so re-picking a thumbnail has to remount it. Keyed on the open
            // counter rather than the image: re-picking one already viewed
            // otherwise reconciles in place and leaves the gallery on whatever
            // image the arrows last landed on.
            key={selection.openId}
            images={selection.images}
            initialIndex={selection.initialIndex}
            onImageChange={setActiveIndex}
            showHeader={false}
          />
        </View>
      ),
      // Stable across image changes, unlike the id SlideLayout would otherwise
      // derive from the (per-image) title.
      debug: {
        id: 'routine-image',
        titleKey: 'routines.admin.image',
        component: 'ImageGallery',
        columnSource: PANEL_SOURCE,
        entity: { type: 'routineImage' },
      },
    };
  }, [selection, activeIndex, closeImagePanel]);

  return { onViewImages, imageColumn, closeImagePanel };
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.role.canvas,
    // The gallery caps its image against the viewport, not its container, and
    // stacks a thumbnail strip under it — so in a panel shorter than the
    // viewport the pair can exceed this box.
    overflow: 'hidden',
  },
});
